import 'font-awesome/css/font-awesome.min.css';
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import * as XLSX from "xlsx";
import "../../../Styles/CreateChecklist.css";

const BASE_URL = process.env.REACT_APP_API_URL;


// Add a toast configuration object for consistent styling
const toastConfig = {
  position: "top-right",
  autoClose: 3000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true
};

// Add these file preview functions to your component
const getPreviewIcon = (fileType) => {
  if (fileType.startsWith('image/')) {
    return 'fa-file-image';
  } else if (fileType === 'application/pdf') {
    return 'fa-file-pdf';
  } else if (fileType.includes('word')) {
    return 'fa-file-word';
  }
  return 'fa-file';
};

const getPreviewColor = (fileType) => {
  if (fileType.startsWith('image/')) {
    return 'text-success';
  } else if (fileType === 'application/pdf') {
    return 'text-danger';
  } else if (fileType.includes('word')) {
    return 'text-primary';
  }
  return 'text-secondary';
};

const BusinessCreateChecklist = ({ isEditing }) => {
  const { id } = useParams();
  const [step, setStep] = useState(1);
  const [details, setDetails] = useState({
    checklistName: "",
    description: "",
  });

  const [sections, setSections] = useState([
    { name: "", subsections: [], visible: true, questions: [] },
  ]);
  
  const [file, setFile] = useState(null);
  const [existingFile, setExistingFile] = useState(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState(null);
  const [deleteExistingFile, setDeleteExistingFile] = useState(false);
  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState(new Set());
  const [tempSelectedQuestions, setTempSelectedQuestions] = useState(new Set());
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [selectedSectionIndex, setSelectedSectionIndex] = useState(null);
  const [selectedSubsectionIndex, setSelectedSubsectionIndex] = useState([]);
  const [optionsVisibility, setOptionsVisibility] = useState({});
  const [uploadedFile, setUploadedFile] = useState(null);
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [manualQuestions, setManualQuestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  

  const navigate = useNavigate();

  const userData = JSON.parse(localStorage.getItem("user") || "{}");
  const CompanyCode = userData?.CompanyCode;

  // Move FilePreview component inside CreateChecklist
  const FilePreview = ({ file, onRemove, onView }) => {
    return (
      <div className="border rounded p-2 mb-2 d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center">
          <i className={`fas ${getPreviewIcon(file.type)} ${getPreviewColor(file.type)} fa-2x me-2`}></i>
          <div>
            <div className="text-truncate" style={{ maxWidth: '200px' }}>
              {file.name}
            </div>
            <small className="text-muted">
              {file.size ? (file.size / (1024 * 1024)).toFixed(2) + ' MB' : ''}
            </small>
          </div>
        </div>
        <div>
          <button 
            className="btn btn-sm btn-outline-primary me-1" 
            onClick={() => onView(file)}
            title="View file"
          >
            <i className="fas fa-eye"></i>
          </button>
          <button 
            className="btn btn-sm btn-outline-danger" 
            onClick={() => onRemove(file)}
            title="Remove file"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
      </div>
    );
  };

  // Update file handling functions
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    
    if (!selectedFile) return;
  
    if (selectedFile.size > 2 * 1024 * 1024) {
      toast.error("File size should be less than 2MB", toastConfig);
      e.target.value = ''; // Reset input
      return;
    }
  
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'image/jpeg',
      'image/png'
    ];
  
    if (!allowedTypes.includes(selectedFile.type)) {
      toast.error("Invalid file type. Only PDF, DOC, DOCX, JPG and PNG files are allowed", toastConfig);
      e.target.value = ''; // Reset input
      return;
    }
  
    // Remove existing file if any
    if (file) {
      URL.revokeObjectURL(filePreviewUrl);
      setFilePreviewUrl(null);
    }
  
    // Remove existing file if present
    setExistingFile(null);
    
    // Set new file
    setFile(selectedFile);
    
    // Create preview URL for images
    if (selectedFile.type.startsWith('image/')) {
      setFilePreviewUrl(URL.createObjectURL(selectedFile));
    }
  
    // Reset the input value to allow selecting the same file again
    e.target.value = '';
  };

  // const handleFileRemove = (fileToRemove) => {
  //   if (fileToRemove === file) {
  //     setFile(null);
  //     if (filePreviewUrl) {
  //       URL.revokeObjectURL(filePreviewUrl);
  //       setFilePreviewUrl(null);
  //     }
  //   } else if (fileToRemove.url === existingFile?.url) {
  //     setExistingFile(null);
  //     setDeleteExistingFile(true); // Mark the existing file for deletion
  //   }
  // };

const handleFileRemove = async (fileToRemove) => {
  // If removing a newly added file (not yet uploaded to server)
  if (fileToRemove === file) {
    setFile(null);
    if (filePreviewUrl) {
      URL.revokeObjectURL(filePreviewUrl);
      setFilePreviewUrl(null);
    }
    return;
  } 
  
  // If removing an existing file that's stored on the server
  if (fileToRemove.url === existingFile?.url) {
    // Extract filename from URL
    const filename = existingFile.url.split('/').pop();
    
    if (!filename) {
      console.error("Could not extract filename from URL:", existingFile.url);
      return;
    }
    
    try {
      // Call the server to delete the file
      const response = await fetch(`${BASE_URL}/delete-business-checklist-file`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          checklistId: id,
          filePath: existingFile.url.replace(`${BASE_URL}/`, '')
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        // Update state to remove the file
        setExistingFile(null);
        setDeleteExistingFile(true);
        
      } else {
        throw new Error(result.message || 'Failed to delete file');
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error(`Error deleting file: ${error.message}`, toastConfig);
    }
  }
};

  const handleFileView = (file) => {
    if (file.type.startsWith('image/')) {
      window.open(file.url || URL.createObjectURL(file), '_blank');
    } else if (file.url) {
      window.open(file.url, '_blank');
    } else {
      const url = URL.createObjectURL(file);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  // Update the validateDuplicates function to include structure validation
  const validateDuplicatesAndStructure = () => {
    const errors = [];
    
    // Check for duplicate sections (branches)
    const sectionNames = sections
      .map(section => section.name)
      .filter(name => name !== "");
    
    const duplicateSections = sectionNames.filter(
      (name, index) => sectionNames.indexOf(name) !== index
    );
    
    if (duplicateSections.length > 0) {
      errors.push(`Duplicate branches found: ${duplicateSections.join(", ")}`);
    }

    // Validate sections against company structure
    sections.forEach(section => {
      if (!section.name) return;

      // Check if branch exists in company structure
      if (!branches.some(branch => branch.branchName === section.name)) {
        errors.push(`Branch "${section.name}" is not in your company structure`);
      }

      // Check for duplicate departments within each section
      const departmentNames = section.subsections
        .map(sub => sub.name)
        .filter(name => name !== "");
      
      const duplicateDepartments = departmentNames.filter(
        (name, index) => departmentNames.indexOf(name) !== index
      );
      
      if (duplicateDepartments.length > 0) {
        errors.push(`Duplicate departments found in branch "${section.name}": ${duplicateDepartments.join(", ")}`);
      }

      // Validate departments against company structure
      section.subsections.forEach(subsection => {
        if (!subsection.name) return;

        // Check if department exists in company structure
        if (!departments.some(dept => dept.departmentName === subsection.name)) {
          errors.push(`Department "${subsection.name}" in branch "${section.name}" is not in your company structure`);
        }
      });
    });

    return errors;
  };

  useEffect(() => {
    if (!CompanyCode) {
      console.error("User Branch Code is missing!");
      return;
    }
  
    // Fetch branches
    const fetchBranches = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`${BASE_URL}/get-business-branches?CompanyCode=${CompanyCode}`);
        if (!response.ok) throw new Error("Failed to fetch branches");
        const data = await response.json();
        setBranches(data);  // Store fetched branches
      } catch (error) {
        console.error("Error fetching branches:", error);
        toast.error("Failed to load branches", toastConfig);
      } finally {
        setIsLoading(false);
      }
    };
  
    // Fetch departments
    const fetchDepartments = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`${BASE_URL}/get-business-departments?CompanyCode=${CompanyCode}`);
        if (!response.ok) throw new Error("Failed to fetch departments");
        const data = await response.json();
        setDepartments(data);  // Store fetched departments
      } catch (error) {
        console.error("Error fetching departments:", error);
        toast.error("Failed to load departments", toastConfig);
      } finally {
        setIsLoading(false);
      }
    };
  
    fetchBranches();
    fetchDepartments();
  }, [CompanyCode]);

  // Add effect to fetch checklist data when editing
  useEffect(() => {
    const fetchChecklistData = async () => {
      if (!isEditing || !id || !CompanyCode) return;

      try {
        setIsLoading(true);
        const response = await fetch(`${BASE_URL}/get-business-checklist/${id}`);
        if (!response.ok) throw new Error('Failed to fetch checklist');
        
        const checklistData = await response.json();
        
        // Set basic details
        setDetails({
          checklistName: checklistData.checklist_name,
          description: checklistData.description,
        });

        // Parse sections
        try {
          const parsedSections = typeof checklistData.sections === 'string' 
            ? JSON.parse(checklistData.sections) 
            : checklistData.sections;
          setSections(parsedSections);
          
          // Get all question IDs from sections
          const questionIds = new Set();
          const extractQuestionIds = (section) => {
            if (section.questions) {
              section.questions.forEach(qId => questionIds.add(qId));
            }
            if (section.subsections) {
              section.subsections.forEach(sub => extractQuestionIds(sub));
            }
          };
          parsedSections.forEach(section => extractQuestionIds(section));
          
          // Set selected questions
          setSelectedQuestions(questionIds);
          
          // Set filtered questions if questions data is available
          if (checklistData.questions) {
            setFilteredQuestions(checklistData.questions.map(q => ({
              id: q.id,
              questionText: q.question_text,
              answerType: q.answer_type,
              answerOptions: q.answer_options,
              optionPoints: q.option_points,
              CompanyCode: q.CompanyCode
            })));
          }
        } catch (error) {
          console.error('Error parsing sections:', error);
          toast.error('Error loading checklist structure');
        }

        // Handle file if exists
        if (checklistData.file_path) {
          setExistingFile({
            name: checklistData.file_path.split('/').pop(),
            url: `${BASE_URL}/${checklistData.file_path}`
          });
        }

      } catch (error) {
        console.error("Error fetching checklist:", error);
        toast.error(`Failed to load checklist: ${error.message}`, toastConfig);
      } finally {
        setIsLoading(false);
      }
    };

    fetchChecklistData();
  }, [id, isEditing, CompanyCode]);

  // Modify the fetchQuestionsForSection effect
  useEffect(() => {
    const fetchQuestionsForSection = async () => {
      if (selectedSectionIndex === null || !CompanyCode) return;

      try {
        const currentQuestions = getCurrentSectionQuestions();
        if (!currentQuestions || currentQuestions.length === 0) return;


        // Fetch questions data for the current section
        const response = await fetch(`${BASE_URL}/get-business-questions-by-ids`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            questionIds: currentQuestions,
            CompanyCode: CompanyCode
          })
        });

        if (!response.ok) throw new Error('Failed to fetch questions');
        
        const questionData = await response.json();

        // Update filteredQuestions with the fetched data
        setFilteredQuestions(prev => {
          const nonSectionQuestions = prev.filter(q => !currentQuestions.includes(q.id));
          return [...nonSectionQuestions, ...questionData.map(q => ({
            id: q.id,
            questionText: q.question_text,
            answerType: q.answer_type,
            answerOptions: q.answer_options,
            optionPoints: q.option_points,
            CompanyCode: q.CompanyCode
          }))];
        });

      } catch (error) {
        console.error("Error fetching section questions:", error);
        toast.error("Failed to load section questions", toastConfig);
      }
    };

    fetchQuestionsForSection();
  }, [selectedSectionIndex, selectedSubsectionIndex, CompanyCode]);

const handleGenerateExcel = async () => {
  try {
    // Fetch company structure data
    const response = await fetch(`${BASE_URL}/get-business-company-structure?CompanyCode=${CompanyCode}`);
    if (!response.ok) {
      throw new Error('Failed to fetch company structure');
    }
    const structureData = await response.json();

    const excelData = [];

    // Loop through each branch in the structure
    structureData.forEach((branch) => {
      // Use the departments array directly from the response
      branch.departments.forEach((department) => {
        // Create examples with sub-subsections
        const subSubSections = ['Process 1', 'Process 2']; // Example sub-subsections

        subSubSections.forEach((subSubSection) => {
          // Create some dummy data for the questions
          excelData.push([
            branch.branchName,           // Section (Branch)
            department,                  // Subsection (Department)
            subSubSection,              // Sub-subsection
            `Question for ${subSubSection} in ${department}`,  // Question
            "FreeText",                 // AnswerType
            "",                         // AnswerOptions (empty for FreeText)
            ""                          // AnswerPoints (empty for FreeText)
          ]);

          // Add example questions with different answer types
          excelData.push([
            branch.branchName,
            department,
            subSubSection,
            `Question 2 for ${subSubSection}`,
            "SingleOption",
            "Yes,No",
            "10,0"
          ]);

          excelData.push([
            branch.branchName,
            department,
            subSubSection,
            `Question 3 for ${subSubSection}`,
            "MultipleOptions",
            "Option A,Option B,Option C",
            "5,3,2"
          ]);

          excelData.push([
            branch.branchName,
            department,
            subSubSection,
            `Question 4 for ${subSubSection}`,
            "Dropdown",
            "Option 1,Option 2,Option 3",
            "4,3,3"
          ]);
        });
      });
    });

    // Generate Excel file with bold headers
    const ws = XLSX.utils.aoa_to_sheet([
      ["Section", "Subsection", "Sub-subsection", "Question", "AnswerType", "AnswerOptions", "AnswerPoints"],
      ...excelData
    ]);

    // Bold the header row
    const headerRange = { s: { r: 0, c: 0 }, e: { r: 0, c: 6 } };
    for (let c = headerRange.s.c; c <= headerRange.e.c; c++) {
      const cell = ws[XLSX.utils.encode_cell({ r: 0, c })];
      if (cell) {
        cell.s = { font: { bold: true } };
      }
    }

    // Set column widths
    ws['!cols'] = [
      { width: 15 },  // Section
      { width: 15 },  // Subsection
      { width: 15 },  // Sub-subsection
      { width: 40 },  // Question
      { width: 15 },  // AnswerType
      { width: 30 },  // AnswerOptions
      { width: 15 }   // AnswerPoints
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Checklist Data");

    // Export to file
    XLSX.writeFile(wb, "Checklist_template.xlsx");

  } catch (error) {
    console.error("Error generating Excel template:", error);
    toast.error("Failed to generate Excel template", toastConfig);
  }
};
  
  


  useEffect(() => {
    if (!CompanyCode) {
      console.error("User Branch Code is missing!");
      return;
    }

    const fetchManualQuestions = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(
          `${BASE_URL}/get-business-manual-questions?CompanyCode=${CompanyCode}`
        );
        
        if (!response.ok) throw new Error("Failed to fetch questions");
        
        const { data } = await response.json();
        
        // Transform the data to match the expected format
        const formattedQuestions = data.map(question => ({
          id: question.id,
          questionText: question.questionText,
          answerType: question.answerType,
          answerOptions: question.answerOptions,
          optionPoints: question.optionPoints,
          CompanyCode: question.CompanyCode
        }));
        
        setManualQuestions(formattedQuestions);
        
        // Update filtered questions while preserving Excel-imported ones
        setFilteredQuestions(prev => {
          const excelQuestions = prev.filter(q => q.isFromExcel);
          return [...excelQuestions, ...formattedQuestions];
        });
      } catch (error) {
        console.error("Error fetching manual questions:", error);
        // toast.error("Failed to load manual questions", toastConfig);
      } finally {
        setIsLoading(false);
      }
    };

    fetchManualQuestions();
  }, [CompanyCode]);


  // Update selectedQuestions whenever sections change
  useEffect(() => {
    const gatherQuestions = (sections) => {
      let allQuestions = [];
      sections.forEach((section) => {
        allQuestions = allQuestions.concat(section.questions);
        if (section.subsections && section.subsections.length > 0) {
          allQuestions = allQuestions.concat(gatherQuestions(section.subsections));
        }
      });
      return allQuestions;
    };

    const allQuestions = gatherQuestions(sections);
    setSelectedQuestions(new Set(allQuestions));
  }, [sections]);

  // Question component to render different answer types
  const QuestionWithAnswerType = ({ question }) => {
    const [isExpanded, setIsExpanded] = useState(false);
  
    if (!question) return <p>Question not found</p>;
    
    const getAnswerType = (question) => {
      return question.answer_type || question.answerType;
    };
  
    const getOptionsAndPoints = () => {
      let options = [];
      let points = [];
  
      // Handle the string format: "item1,item2,item3,item4"
      if (question.answer_options) {
        // Remove quotes and split by comma
        options = question.answer_options.replace(/^"|"$/g, '').split(',');
      } else if (question.answerOptions) {
        options = typeof question.answerOptions === 'string'
          ? question.answerOptions.replace(/^"|"$/g, '').split(',')
          : question.answerOptions;
      }
  
      if (question.optionPoints) {
        points = question.optionPoints.replace(/^"|"$/g, '').split(',');
      } else if (question.answerPoints) {
        points = typeof question.answerPoints === 'string'
          ? question.answerPoints.replace(/^"|"$/g, '').split(',')
          : question.answerPoints;
      }
  
      // Clean up the arrays
      options = options.map(opt => opt.trim());
      points = points.map(pt => pt.trim());
  
      return options.map((option, index) => ({
        option: option,
        points: points[index] || '0'
      }));
    };
    
    return (
      <div className="question-container border rounded p-3">
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div className="d-flex align-items-center flex-grow-1">
            <i className="fas fa-question-circle text-primary me-2"></i>
            <span className="fw-medium">{question.questionText}</span>
          </div>
          <div className="d-flex align-items-center ms-3">
            <span className="badge bg-info me-2">
              {getAnswerType(question)}
            </span>
            <button 
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`}></i>
            </button>
          </div>
        </div>
        
        {isExpanded && getAnswerType(question) !== 'FreeText' && (
          <div className="mt-3 border-top pt-3">
            <div className="table-responsive">
              <table className="table table-sm table-bordered mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Option</th>
                    <th width="100" className="text-center">Points</th>
                  </tr>
                </thead>
                <tbody>
                  {getOptionsAndPoints().map((item, index) => (
                    <tr key={index}>
                      <td>{item.option}</td>
                      <td className="text-center">
                        <span className="badge bg-success">
                          {item.points}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        
        {isExpanded && getAnswerType(question) === 'FreeText' && (
          <div className="mt-3 border-top pt-3">
            <div className="text-muted">
              <i className="fas fa-info-circle me-2"></i>
              This is a free text question that requires a written answer.
            </div>
          </div>
        )}
      </div>
    );
  };

  // Enhanced Excel File Upload
  const handleExcelUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadedFile(file);
    
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const binaryStr = event.target.result;
        const workbook = XLSX.read(binaryStr, { type: "binary" });
        const sheetName = workbook.SheetNames[0]; // Assume data is in the first sheet
        const sheet = workbook.Sheets[sheetName];
        const data = XLSX.utils.sheet_to_json(sheet);
        
        if (data.length === 0) {
          toast.error("The Excel file appears to be empty or has no valid data.", toastConfig);
          return;
        }
        
        // Validate required columns
        const requiredColumns = ["Section", "Question", "AnswerType"];
        const firstRow = data[0];
        const missingColumns = requiredColumns.filter(col => !(col in firstRow));
        
        if (missingColumns.length > 0) {
          toast.error(`The Excel file is missing required columns: ${missingColumns.join(", ")}`, toastConfig);
          return;
        }
        
        processExcelData(data);
        toast.success(`Successfully imported ${data.length} questions from Excel file.`, toastConfig);
      } catch (error) {
        console.error("Error processing Excel file:", error);
        toast.error("Failed to process the Excel file. Please check the file format.", toastConfig);
      }
    };

    reader.readAsBinaryString(file);
  };


  const processExcelData = async (data) => {
    const newSections = [];
    const newFilteredQuestions = [...filteredQuestions];
    const newSelectedQuestions = new Set(selectedQuestions);
    const usedIds = new Set(); // Track used IDs
    
    try {
      // Process each row sequentially to avoid timing issues
      for (let index = 0; index < data.length; index++) {
        const row = data[index];
        const { Section, Subsection, "Sub-subsection": SubSubsection, Question, AnswerType, AnswerOptions, AnswerPoints } = row;
  
        // Skip if any required fields are missing
        if (!Section || !Question || !AnswerType) {
          console.warn("Skipping row with missing required fields:", row);
          continue;
        }
  
        const questionId = `q${(Date.now() % 1000000000)}${Math.floor(Math.random() * 1000)}`;
  
        const questionEntry = {
          id: questionId,
          questionText: Question,
          answerType: AnswerType,
          answerOptions: AnswerOptions ? AnswerOptions.split(",").map(opt => opt.trim()) : [],
          answerPoints: AnswerPoints ? AnswerPoints.split(",").map(pt => pt.trim()) : [],
          CompanyCode: CompanyCode,
          isFromExcel: true
        };
  
        try {
          // Save question to database
          await saveQuestionToDatabase(questionEntry);
          
          // Add to filtered questions after successful save
          newFilteredQuestions.push(questionEntry);
          newSelectedQuestions.add(questionId);
  
          // Find or create section (Branch)
          let section = newSections.find(s => s.name === Section);
          if (!section) {
            section = { name: Section, subsections: [], visible: true, questions: [] };
            newSections.push(section);
          }
  
          if (Subsection) {
            // Find or create subsection (Department)
            let subsection = section.subsections.find(sub => sub.name === Subsection);
            if (!subsection) {
              subsection = { name: Subsection, subsections: [], visible: true, questions: [] };
              section.subsections.push(subsection);
            }
  
            if (SubSubsection) {
              // Find or create sub-subsection
              let subSubsection = subsection.subsections.find(sub => sub.name === SubSubsection);
              if (!subSubsection) {
                subSubsection = { name: SubSubsection, subsections: [], visible: true, questions: [] };
                subsection.subsections.push(subSubsection);
              }
              subSubsection.questions.push(questionId);
            } else {
              // Add question to subsection if no sub-subsection specified
              subsection.questions.push(questionId);
            }
          } else {
            // Add question to main section if no subsection specified
            section.questions.push(questionId);
          }
  
        } catch (error) {
          console.error(`Failed to save question: ${Question}`, error);
          continue; // Skip this question and continue with the next
        }
      }
  
      // Update state only after all questions are processed
      setSections(newSections.length > 0 ? newSections : sections);
      setFilteredQuestions(newFilteredQuestions);
      setSelectedQuestions(newSelectedQuestions);
      
      // toast.success(`Successfully processed ${data.length} questions`, toastConfig);
    } catch (error) {
      console.error("Error processing Excel data:", error);
      toast.error("Failed to process Excel data", toastConfig);
    }
  };

  const saveQuestionToDatabase = async (question) => {
    try {
      const response = await fetch(`${BASE_URL}/add-business-question`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: question.id,
          questionText: question.questionText,
          answerType: question.answerType,
          answerOptions: question.answerOptions,
          optionPoints: question.answerPoints,
          CompanyCode: CompanyCode,
          addingMethod: 'auto'
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save question');
      }

      return await response.json();
    } catch (error) {
      console.error('Error saving question:', error);
      throw error;
    }
  };

  // Update the handleNext function
  const handleNext = () => {
    if (step === 1) {
      if (!details.checklistName || !details.description) {
        toast.warning("Please fill out all details before proceeding.", toastConfig);
        return;
      }
    }
  
    if (step === 2) {
      // Check for empty sections
      const hasEmptySections = sections.some(section => !section.name);
      if (hasEmptySections) {
        toast.error("Please fill out all branch names before proceeding.", toastConfig);
        return;
      }
  
      // Check for empty departments
      const hasEmptyDepartments = sections.some(
        section => section.subsections.some(sub => !sub.name)
      );
      if (hasEmptyDepartments) {
        toast.error("Please fill out all department names before proceeding.", toastConfig);
        return;
      }
  
      // Check for duplicates and structure validation
      const errors = validateDuplicatesAndStructure();
      if (errors.length > 0) {
        errors.forEach(error => toast.error(error, toastConfig));
        return;
      }
    }
  
    setStep((prev) => prev + 1);
  };

  const handlePrevious = () => {
    setStep((prev) => prev - 1);
  };

  // Update the handleSave function to avoid saving duplicate questions
  const handleSave = async () => {
    try {
      if (!CompanyCode) {
        toast.error("User Branch Code is missing!", toastConfig);
        return;
      }

      // Create FormData and append file
      const formData = new FormData();
      
      // Append file with specific handling
      if (file) {
        formData.append("file", file);
        formData.append("fileType", file.type);
        formData.append("fileName", file.name);
      }
      formData.append("deleteExistingFile", deleteExistingFile);

      // Append other data
      formData.append("checklistName", details.checklistName);
      formData.append("description", details.description);
      formData.append("sections", JSON.stringify(sections));
      formData.append("selectedQuestions", JSON.stringify([...selectedQuestions]));
      formData.append("CompanyCode", CompanyCode);

      const url = isEditing
        ? `${BASE_URL}/update-business-checklist/${id}`
        : `${BASE_URL}/add-business-checklist`;

      const response = await fetch(url, {
        method: isEditing ? "PUT" : "POST",
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to save checklist");
      }

      toast.success(
        isEditing ? "Checklist updated successfully!" : "Checklist created successfully!",
        {
          ...toastConfig,
          onClose: () => navigate("/BusinessChecklistOverview", { state: { CompanyCode } }),
        }
      );

    } catch (error) {
      console.error("Error saving data:", error);
      toast.error(`Error: ${error.message}`, toastConfig);
    }
  };

  const validateBranch = (branchName) => {
    return branches.some(branch => branch.branchName === branchName);
  };
  
  const validateDepartment = (departmentName) => {
    return departments.some(dept => dept.departmentName === departmentName);
  };

  const handleSelectAll = () => {
    setTempSelectedQuestions(prev => {
      const newSelected = new Set(prev);
      const allSelected = manualQuestions.every(q => newSelected.has(q.id));
      
      if (allSelected) {
        // Deselect all
        newSelected.clear();
      } else {
        // Select all
        manualQuestions.forEach(q => newSelected.add(q.id));
      }
      return newSelected;
    });
  };

  const handleQuestionSelect = (questionId) => {
    setTempSelectedQuestions(prev => {
      const newSelected = new Set(prev);
      if (newSelected.has(questionId)) {
        newSelected.delete(questionId);
      } else {
        newSelected.add(questionId);
      }
      return newSelected;
    });
  };
  
  const assignQuestionsToSection = async () => {
    if (selectedSectionIndex === null) {
      toast.error("Please select a section first", toastConfig);
      return;
    }
  
    try {
      const selectedIds = Array.from(tempSelectedQuestions);
  
      if (selectedIds.length === 0) {
        toast.error("No questions selected", toastConfig);
        return;
      }
  
      // Update sections with the selected questions
      setSections(prevSections => {
        const newSections = [...prevSections];
        let target = newSections[selectedSectionIndex];
  
        // Navigate to target subsection if needed
        if (selectedSubsectionIndex.length > 0) {
          for (const index of selectedSubsectionIndex) {
            target = target.subsections[index];
          }
        }
  
        // Initialize questions array if it doesn't exist
        if (!target.questions) {
          target.questions = [];
        }
  
        // Add new questions
        target.questions = [...new Set([...target.questions, ...selectedIds])];
  
        return newSections;
      });
  
      // Close modal and clear selections
      setShowQuestionModal(false);
      setTempSelectedQuestions(new Set());
  
      // Fetch updated questions data
      await fetchQuestionsData(selectedIds);
      
      // Force re-render of questions
      setOptionsVisibility({}); // Reset options visibility state
  
      toast.success("Questions added successfully", toastConfig);
  
    } catch (error) {
      console.error('Error assigning questions:', error);
      toast.error('Failed to assign questions', toastConfig);
    }
  };

  const removeQuestionFromSection = (questionId) => {
    if (selectedSectionIndex === null) return;

    setSections((prevSections) => {
      const updatedSections = [...prevSections];
      let target = updatedSections[selectedSectionIndex];

      if (selectedSubsectionIndex.length > 0) {
        selectedSubsectionIndex.forEach((index) => {
          target = target.subsections[index];
        });
      }

      target.questions = target.questions.filter((qId) => qId !== questionId);

      return updatedSections;
    });
  };

  const toggleOptionsVisibility = (questionId) => {
    setOptionsVisibility((prevState) => ({
      ...prevState,
      [questionId]: !prevState[questionId], // Toggle visibility for the specific question
    }));
  };
  
  const toggleVisibility = (section) => {
    section.visible = !section.visible;
    setSections([...sections]);
  };

  const addSubsection = (parentSection) => {
    parentSection.subsections.push({ name: "", subsections: [], visible: true, questions: [] });
    setSections([...sections]);
  };

  const deleteSubsection = (parentSection, index) => {
    parentSection.subsections.splice(index, 1);
    setSections([...sections]);
  };

  const renderSubsections = (subsections, parentSection, indices = []) => {
    return subsections.map((subsection, index) => {
      const currentIndices = [...indices, index];
      const currentLevel = currentIndices.length;

      return (
        <div key={index} className="ms-4 subsection-container">
          <div
            className="d-flex align-items-center mb-1"
            onClick={() => {
              setSelectedSectionIndex(indices[0] ?? null);
              setSelectedSubsectionIndex(currentIndices.slice(1));
            }}
            style={{
              cursor: "pointer",
              backgroundColor:
                selectedSectionIndex === indices[0] &&
                JSON.stringify(selectedSubsectionIndex) ===
                JSON.stringify(currentIndices.slice(1))
                  ? "#f0f0f0"
                  : "transparent",
              padding: "4px",
              borderRadius: "4px",
            }}
          >
            <button
              className="btn btn-secondary me-1"
              onClick={(e) => {
                e.stopPropagation();
                toggleVisibility(subsection);
              }}
            >
              <i className={`fas ${subsection.visible ? "fa-chevron-down" : "fa-chevron-right"}`}></i>
            </button>
            
            {/* For level 1 (departments), show dropdown */}
            {currentLevel === 1 ? (
              <select
                className="form-select me-1"
                value={subsection.name}
                onChange={(e) => {
                  subsection.name = e.target.value;
                  setSections([...sections]);
                }}
              >
                <option value="">Select Department</option>
                {departments.map((dept) => (
                  <option key={dept.departmentId} value={dept.departmentName}>
                    {dept.departmentName}
                  </option>
                ))}
              </select>
            ) : (
              // For level 2 and beyond, keep as text input
              <input
                type="text"
                className="form-control me-1 subsection-input"
                placeholder="Sub-section Name"
                value={subsection.name}
                onChange={(e) => {
                  subsection.name = e.target.value;
                  setSections([...sections]);
                }}
              />
            )}

            <button
              className="btn btn-danger me-1"
              onClick={(e) => {
                e.stopPropagation();
                deleteSubsection(parentSection, index);
              }}
            >
              <i className="fa fa-trash-alt"></i>
            </button>
            {currentLevel < 3 && (
              <button
                className="btn btn-success"
                onClick={(e) => {
                  e.stopPropagation();
                  addSubsection(subsection);
                }}
              >
                <i className="fa fa-plus"></i>
              </button>
            )}
          </div>

          {subsection.visible &&
            renderSubsections(subsection.subsections, subsection, currentIndices)}
        </div>
      );
    });
  };

  const isAllSelected = selectedQuestions.size === filteredQuestions.length;

  const getCurrentSectionQuestions = () => {
    try {
      if (selectedSectionIndex === null || !sections[selectedSectionIndex]) {
        return [];
      }

      let target = sections[selectedSectionIndex];

      // If subsection is selected, traverse to it
      if (selectedSubsectionIndex.length > 0) {
        for (const index of selectedSubsectionIndex) {
          if (!target?.subsections?.[index]) {
            console.warn('Invalid subsection path');
            return [];
          }
          target = target.subsections[index];
        }
      }

      // Get questions from the current section/subsection
      return target?.questions || [];
    } catch (error) {
      console.error('Error in getCurrentSectionQuestions:', error);
      return [];
    }
  };

  useEffect(() => {
    const currentQuestions = getCurrentSectionQuestions();
    if (currentQuestions.length > 0) {
      fetchQuestionsData(currentQuestions);
    }
  }, [selectedSectionIndex, selectedSubsectionIndex]); // Add other dependencies if needed

  const fetchQuestionsData = async (questionIds) => {
    if (!questionIds || questionIds.length === 0) return;

    try {
      setIsLoading(true);
      
      const response = await fetch(`${BASE_URL}/get-business-questions-by-ids`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          questionIds: questionIds
        })
      });

      if (!response.ok) {
        throw new Error('Failed to fetch questions');
      }

      const data = await response.json();

      if (data && data.length > 0) {
        setFilteredQuestions(prev => {
          // Create a map of existing questions
          const existingQuestions = new Map(prev.map(q => [q.id, q]));
          
          // Add or update questions from the response
          data.forEach(q => {
            existingQuestions.set(q.id, {
              id: q.id,
              questionText: q.questionText,
              answerType: q.answerType,
              answerOptions: q.answerOptions,
              optionPoints: q.optionPoints,
              CompanyCode: q.CompanyCode
            });
          });

          const updatedQuestions = Array.from(existingQuestions.values());
          return updatedQuestions;
        });
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
      toast.error('Failed to load questions');
    } finally {
      setIsLoading(false);
    }
  };

  const renderQuestions = () => {
    const currentQuestions = getCurrentSectionQuestions();
    
    if (!currentQuestions || currentQuestions.length === 0) {
      return (
        <div className="text-center p-4">
          <i className="fas fa-info-circle text-muted fs-4 mb-2"></i>
          <p className="text-muted">No questions added to this section yet.</p>
          <button
            className="btn btn-outline-primary"
            onClick={() => setShowQuestionModal(true)}
          >
            <i className="fas fa-plus me-2"></i>Add Questions
          </button>
        </div>
      );
    }
  
    return currentQuestions.map((qId) => {
      const question = filteredQuestions.find((q) => q.id === qId);
      const isExpanded = optionsVisibility[qId] || false;
      
      if (!question) {
        return (
          <div key={qId} className="list-group-item mb-3">
            <div className="d-flex align-items-center">
              <div className="spinner-border spinner-border-sm me-2" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <span>Loading question data...</span>
            </div>
          </div>
        );
      }
  
      // Handle different formats of answerOptions and points
      const options = (() => {
        if (!question.answerOptions) return [];
        if (Array.isArray(question.answerOptions)) return question.answerOptions;
        try {
          return JSON.parse(question.answerOptions);
        } catch {
          return question.answerOptions.split(',').map(opt => opt.trim());
        }
      })();
  
      const points = (() => {
        if (!question.optionPoints) return [];
        if (Array.isArray(question.optionPoints)) return question.optionPoints;
        try {
          return JSON.parse(question.optionPoints);
        } catch {
          return question.optionPoints.split(',').map(p => Number(p.trim()) || 0);
        }
      })();
  
      return (
        <div key={qId} className="list-group-item mb-3">
          <div className="question-header d-flex justify-content-between align-items-start p-2" 
               onClick={() => toggleOptionsVisibility(qId)}
               style={{ cursor: 'pointer' }}>
            <div className="d-flex align-items-center flex-grow-1">
              <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'} me-2`}></i>
              <div>
                <h6 className="mb-1">{question.questionText}</h6>
                <span className="badge bg-info">{question.answerType}</span>
              </div>
            </div>
            <button
              className="btn btn-sm btn-danger ms-2"
              onClick={(e) => {
                e.stopPropagation();
                removeQuestionFromSection(qId);
              }}
            >
              <i className="fas fa-trash-alt"></i>
            </button>
          </div>
  
          {isExpanded && (
            <div className="question-details p-3 border-top mt-2">
              {question.answerType !== 'FreeText' && options.length > 0 && (
                <div className="options-table">
                  <table className="table table-sm table-bordered mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Option</th>
                        <th width="100" className="text-center">Points</th>
                      </tr>
                    </thead>
                    <tbody>
                      {options.map((opt, idx) => (
                        <tr key={idx}>
                          <td>{opt}</td>
                          <td className="text-center">
                            <span className="badge bg-success">
                              {points[idx] || 0}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {question.answerType === 'FreeText' && (
                <div className="alert alert-light mb-0">
                  <i className="fas fa-info-circle me-2"></i>
                  This is a free text question that requires a written response.
                </div>
              )}
            </div>
          )}
        </div>
      );
    });
  };

  const currentQuestions = getCurrentSectionQuestions();

  // Add this helper function to check if a question belongs to current section
  const isQuestionInCurrentSection = (questionId) => {
    if (selectedSectionIndex === null) return false;
    
    let target = sections[selectedSectionIndex];
    
    // Navigate to subsection if needed
    if (selectedSubsectionIndex.length > 0) {
      for (const index of selectedSubsectionIndex) {
        if (!target?.subsections?.[index]) return false;
        target = target.subsections[index];
      }
    }
    
    return target.questions?.includes(questionId) || false;
  };

  return (
    <div className="container-fluid ">
      <div className="container mt-4 text-start" style={{ margin: "0 auto", width: "auto" }}>
        <h2>{isEditing ? 'Edit' : 'Create'} Checklist</h2>

        <div className="container-fluid px-0">
          <div className="step-indicator">
            <div className="step-container">
              <div className={`step ${step >= 1 ? "active" : ""}`}>
                <div className="circle">1</div>
                <span>Details</span>
              </div>
              <div className="line"></div>
            </div>
            <div className="step-container">
              <div className={`step ${step >= 2 ? "active" : ""}`}>
                <div className="circle">2</div>
                <span>Sections</span>
              </div>
              <div className="line"></div>
            </div>
            <div className="step-container">
              <div className={`step ${step === 3 ? "active" : ""}`}>
                <div className="circle">3</div>
                <span>File Upload</span>
              </div>
            </div>
          </div>
        </div>

        {step === 1 && (
          <div>
            <h3>Details</h3>
            <div className="form-group mb-4">
              <label>Checklist Name</label>
              <input
                type="text"
                className="form-control"
                value={details.checklistName}
                onChange={(e) =>
                  setDetails({ ...details, checklistName: e.target.value })
                }
              />
            </div>
            <div className="form-group mb-4">
              <label>Description</label>
              <textarea
                className="form-control"
                value={details.description}
                onChange={(e) =>
                  setDetails({ ...details, description: e.target.value })
                }
              ></textarea>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="container-fluid p-0">
            {/* Excel Template Section */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="card shadow-sm">
                  <div className="card-header bg-info text-white d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                      <i className="fas fa-file-excel me-2"></i>
                      Bulk Question Import
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="row align-items-center">
                      <div className="col-md-6">
                        <div className="mb-3 mb-md-0">
                          <label className="form-label">Upload Excel File</label>
                          <input
                            type="file"
                            className="form-control"
                            onChange={handleExcelUpload}
                            accept=".xlsx,.xls"
                          />
                          <small className="text-muted d-block mt-1">
                            Upload your Excel file containing questions
                          </small>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="d-flex flex-column">
                          <button
                            className="btn btn-outline-primary mb-2"
                            onClick={handleGenerateExcel}
                          >
                            <i className="fas fa-download me-2"></i>
                            Download Template
                          </button>
                          <small className="text-muted">
                            Download the Excel template with the correct format
                          </small>
                        </div>
                      </div>
                    </div>

                    <div className="alert alert-info mt-3 mb-0">
                      <h6 className="alert-heading">
                        <i className="fas fa-info-circle me-2"></i>
                        Excel Format Requirements
                      </h6>
                      <p className="mb-2">Your Excel file should include these columns:</p>
                      <div className="row">
                        <div className="col-md-6">
                          <ul className="mb-0 ps-3">
                            <li><strong>Section</strong> - The name of the main section</li>
                            <li><strong>Subsection</strong> - (Optional) The name of the subsection</li>
                            <li><strong>Question</strong> - The question text</li>
                          </ul>
                        </div>
                        <div className="col-md-6">
                          <ul className="mb-0 ps-3">
                            <li><strong>AnswerType</strong> - Must be one of: "single option", "multiple option", "drop down", or "free text"</li>
                            <li><strong>AnswerOptions</strong> - Comma-separated list of options</li>
                            <li><strong>AnswerPoints</strong> - Points for each option (comma-separated)</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Existing Sections and Questions cards */}
            <div className="row row-cols-1 row-cols-xl-2 g-4">
              {/* Sections Card */}
              <div className="col">
                <div className="card h-100 shadow-sm">
                  <div className="card-header d-flex justify-content-between align-items-center bg-primary text-white">
                    <h5 className="mb-0">
                      <i className="fas fa-layer-group me-2"></i>Sections and Subsections
                    </h5>
                    <button
                      className="btn btn-light btn-sm"
                      onClick={() => setSections([...sections, { name: "", subsections: [], visible: true, questions: [] }])}
                    >
                      <i className="fa fa-plus"></i>
                    </button>
                  </div>
                  <div className="card-body">
                    <div className="sections-container" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                      {sections.map((section, sectionIndex) => (
                        <div key={sectionIndex}>
                          <div
                            className="d-flex align-items-center mb-2"
                            onClick={() => {
                              setSelectedSectionIndex(sectionIndex);
                              setSelectedSubsectionIndex([]);
                            }}
                          >
                            <button
                              className="btn btn-secondary me-2"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleVisibility(section);
                              }}
                            >
                              <i className={`fas ${section.visible ? "fa-chevron-down" : "fa-chevron-right"}`}></i>
                            </button>
                            <select
                              className="form-select me-2"
                              value={section.name}
                              onChange={(e) => {
                                section.name = e.target.value;
                                setSections([...sections]);
                              }}
                            >
                              <option value="">Select Branch</option>
                              {branches.map((branch) => (
                                <option key={branch.branchId} value={branch.branchName}>
                                  {branch.branchName}
                                </option>
                              ))}
                            </select>
                            <button
                              className="btn btn-danger me-2"
                              onClick={() => {
                                sections.splice(sectionIndex, 1);
                                setSections([...sections]);
                              }}
                            >
                              <i className="fa fa-trash-alt"></i>
                            </button>
                            <button
                              className="btn btn-success"
                              onClick={() => addSubsection(section)}
                            >
                              <i className="fa fa-plus"></i>
                            </button>
                          </div>
                          {section.visible && renderSubsections(section.subsections, section, [sectionIndex])}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Questions Card */}
              <div className="col">
                <div className="card h-100 shadow-sm">
                  <div className="card-header d-flex justify-content-between align-items-center bg-success text-white">
                    <h5 className="mb-0">
                      <i className="fas fa-question-circle me-2"></i>Questions
                    </h5>
                    <button
                      className="btn btn-light btn-sm"
                      onClick={() => setShowQuestionModal(true)}
                      disabled={selectedSectionIndex === null}
                    >
                      <i className="fas fa-database me-2"></i>Select from Question Bank
                    </button>
                  </div>
                  <div className="card-body">
                    <div className="questions-container" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                      {selectedSectionIndex !== null ? (
                        <div className="list-group">
                          {renderQuestions()}
                        </div>
                      ) : (
                        <div className="text-center p-4">
                          <i className="fas fa-hand-point-left text-muted fs-4 mb-2"></i>
                          <p className="text-muted">Please select a section or subsection to view and manage its questions.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div>
            <h3>File Upload</h3>
            <div className="card shadow-sm p-4">
              <div className="form-group mb-4">
                <label className="mb-2">Attachment (Max Size: 2MB)</label>
                <div className="input-group">
                  <input
                    type="file"
                    className="form-control"
                    onChange={handleFileChange}
                    accept=".docx,.pdf,.jpg,.jpeg,.png"
                    key={file ? 'has-file' : 'no-file'} // Add this line to force re-render
                  />
                </div>
                <small className="text-muted d-block mt-2">
                  Supported formats: PDF, DOC, DOCX, JPG, PNG
                </small>
              </div>

              <div className="uploaded-files-container">
                {file && (
                  <FilePreview 
                    file={file}
                    onRemove={handleFileRemove}
                    onView={handleFileView}
                  />
                )}
                
                {existingFile && (
                  <FilePreview 
                    file={{
                      name: existingFile.name,
                      type: existingFile.name.match(/\.(jpg|jpeg|png)$/i) ? 'image/' :
                            existingFile.name.match(/\.pdf$/i) ? 'application/pdf' :
                            existingFile.name.match(/\.(doc|docx)$/i) ? 'application/msword' : 'application/octet-stream',
                      size: 0,
                      url: existingFile.url
                    }}
                    onRemove={handleFileRemove}
                    onView={handleFileView}
                  />
                )}
              </div>

              {(!file && !existingFile) && (
                <div className="text-center text-muted p-3">
                  <i className="fas fa-cloud-upload-alt fa-2x mb-2"></i>
                  <p>No files uploaded yet</p>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="d-flex justify-content-between mt-4">
          {step > 1 && (
            <button className="btn btn-secondary" onClick={handlePrevious}>
              Previous
            </button>
          )}
          {step < 3 && (
            <button className="btn btn-primary" onClick={handleNext}>
              Next
            </button>
          )}
          {step === 3 && (
            <button className="btn btn-success" onClick={handleSave}>
              {isEditing ? 'Update' : 'Save'} Checklist
            </button>
          )}
        </div>
      </div>

      {showQuestionModal && (
        <div
          className="modal show"
          tabIndex="-1"
          role="dialog"
          style={{ display: "block", backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title">
                  <i className="fas fa-list-ul me-2"></i>
                  Select Questions from Question Bank
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowQuestionModal(false)}
                ></button>
              </div>
              <div className="modal-body p-0">
                {manualQuestions.length > 0 ? (
                  <div className="list-group list-group-flush">
                    <div className="list-group-item bg-light">
                      <div className="d-flex align-items-center">
                        <input
                          type="checkbox"
                          checked={manualQuestions.every(q => tempSelectedQuestions.has(q.id))}
                          onChange={handleSelectAll}
                          className="form-check-input me-2"
                        />
                        <span className="fw-bold">Select All Questions</span>
                      </div>
                    </div>
                    <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                      {manualQuestions.map((question) => {
                        const isSelected = tempSelectedQuestions.has(question.id);
                        const isInCurrentSection = isQuestionInCurrentSection(question.id);
                        
                        return (
                          <div 
                            key={question.id} 
                            className={`list-group-item list-group-item-action ${isSelected ? 'bg-light' : ''}`}
                          >
                            <div className="d-flex align-items-center">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleQuestionSelect(question.id)}
                                className="form-check-input me-3"
                              />
                              <div className="ms-2 flex-grow-1">
                                <div>
                                  {question.questionText}
                                  {isInCurrentSection && (
                                    <span className="badge bg-info ms-2">Already in section</span>
                                  )}
                                </div>
                                <small className="text-muted">
                                  Type: {question.answer_type || question.answerType}
                                </small>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="text-center p-5">
                    <i className="fas fa-exclamation-circle text-muted fs-1 mb-3"></i>
                    <p className="text-muted mb-0">No manual questions available.</p>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-success"
                  onClick={assignQuestionsToSection}
                  disabled={tempSelectedQuestions.size === 0}
                >
                  <i className="fas fa-plus-circle me-2"></i>
                  Add Selected Questions ({tempSelectedQuestions.size})
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowQuestionModal(false)}
                >
                  <i className="fas fa-times me-2"></i>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export default BusinessCreateChecklist;
